import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { environment } from "../pages/util/baseUrl.js";

const axiosInstance = axios.create({
    baseURL: environment.backendUrl,
    withCredentials: true, // Permite el envío automático de cookies HttpOnly
    headers: {
        'X-App-Context': 'intranet'
    }
});

// Interceptor global para manejar respuestas no autorizadas
axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response && error.response.status === 401) {
            const savedUser = sessionStorage.getItem('user');
            localStorage.removeItem('rememberMe');
            const userType = savedUser ? JSON.parse(savedUser)?.userType : null;
            
            sessionStorage.removeItem('user');
        localStorage.removeItem('rememberMe');
            
            // Redirigir según el tipo de usuario
            if (userType === 'CLIENTE') {
                // Los clientes no necesitan redirigir, el ecommerce maneja su propia sesión
                if (!window.location.pathname.startsWith('/tienda')) {
                    window.location.href = '/tienda';
                }
            } else {
                if (window.location.pathname !== '/admin/login') {
                    window.location.href = '/admin/login';
                }
            }
        }
        return Promise.reject(error);
    }
);

const AuthContext = createContext(null);

let authContextState = { user: null, loading: true, axiosInstance };

export const getAuthContext = () => authContextState;

export const useAuth = () => {
    return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(() => {
        const savedUser = sessionStorage.getItem('user');
        return savedUser ? JSON.parse(savedUser) : null;
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const verifySession = async () => {
            try {
                const response = await axiosInstance.get('/api/auth/me', {
                    validateStatus: (status) => status >= 200 && status < 500
                });

                if (response.status === 200 && response.data?.id && response.data?.username) {
                    const userData = {
                        id: response.data.id,
                        username: response.data.username,
                        role: response.data.role,
                        permissions: response.data.permissions || [],
                        profileImage: response.data.profileImage || null,
                        idEmpresa: response.data.idEmpresa || null,
                        idSucursalOriginal: response.data.idSucursal || null,
                        idSucursal: response.data.idSucursal || null,
                        userType: response.data.userType || 'INTERNO',
                    };
                    setUser(userData);
                    sessionStorage.setItem('user', JSON.stringify(userData));
                } else {
                    setUser(null);
                    sessionStorage.removeItem('user');
                }
            } catch (error) {
                if (error.code === 'ERR_NETWORK' || !error.response) {
                    console.debug("Backend en reposo: Sesión no verificada.");
                } else {
                    console.error("Error verifying session:", error);
                }
                setUser(null);
                sessionStorage.removeItem('user');
            } finally {
                setLoading(false);
            }
        };

        verifySession();
    }, []);

    useEffect(() => {
        authContextState = { user, loading, axiosInstance };
    }, [user, loading]);

    

    // Mantenimiento de sesión por actividad
    useEffect(() => {
        let inactivityTimer;

        const resetTimer = () => {
            clearTimeout(inactivityTimer);
            const isRememberMe = localStorage.getItem('rememberMe') === 'true';
            
            // Si el user está logueado pero NO seleccionó "Mantenerme Logueado" (15 mins de inactividad)
            if (user && !isRememberMe) {
                inactivityTimer = setTimeout(() => {
                    console.log('Sesión expirada por inactividad');
                    logout();
                }, 60 * 60 * 1000); // 60 minutos
            }
        };

        const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
        
        if (user) {
            resetTimer(); // Start timer
            activityEvents.forEach(event => document.addEventListener(event, resetTimer));
        }

        return () => {
            clearTimeout(inactivityTimer);
            activityEvents.forEach(event => document.removeEventListener(event, resetTimer));
        };
    }, [user]);


    const login = async (username, password, rememberMe = false) => {
        try {
            const response = await axiosInstance.post('/api/auth/authenticate', { username, password, rememberMe });
            if (response.status === 200) {
                const userData = {
                    username: username,
                    id: response.data.id,
                    role: response.data.role || 'USER',
                    permissions: response.data.permissions || [],
                    profileImage: response.data.profileImage || null,
                    idEmpresa: response.data.idEmpresa || null,
                    idSucursal: response.data.idSucursal || null,
                    userType: response.data.userType || 'INTERNO',
                };

                setUser(userData);
                sessionStorage.setItem('user', JSON.stringify(userData));
                localStorage.setItem('rememberMe', rememberMe ? 'true' : 'false');

                return userData;
            } else {
                throw new Error('Error en la autenticación.');
            }
        } catch (error) {
            throw error;
        }
    };

    const logout = async () => {
        localStorage.removeItem('rememberMe');
        try {
            await axiosInstance.post('/api/auth/logout');
        } catch (error) {
            // Error silencioso
        } finally {
            setUser(null);
            sessionStorage.removeItem('user');
        }
    };

    const updateUserProfile = (updates) => {
        const updatedUser = { ...user, ...updates };
        setUser(updatedUser);
        sessionStorage.setItem('user', JSON.stringify(updatedUser));
    };

    const value = {
        user,
        loading,
        login,
        logout,
        updateUserProfile,
        axiosInstance
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
